import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import logger from "@/utils/logger";
import { formatDate } from "@/utils/formatting";
import DashboardLayout from "@/components/layout/DashboardLayout";

interface PromoData {
  id: string;
  user_id: string;
  promo_code: string;
  stripe_coupon_id: string | null;
  applied_at: string | null;
  checkout_session_id: string | null;
  subscription_id: string | null;
  status: "pending" | "applied" | "failed" | "abandoned";
  metadata: any;
  created_at: string;
  updated_at: string;
  users?: {
    email: string;
    full_name: string;
  };
  subscriptions?: any;
}

const PromoCodeAnalytics = () => {
  const [promoData, setPromoData] = useState<PromoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [chartData, setChartData] = useState<any[]>([]);
  const [statusData, setStatusData] = useState<any[]>([]);

  // Custom function to fetch promo code data using direct fetch to avoid TypeScript errors
  async function fetchPromoCodeData(): Promise<PromoData[]> {
    // Get the JWT token from Supabase session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }
    
    // Use the REST API directly to avoid TypeScript errors with tables not in the schema
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_promotions?select=*&order=created_at.desc`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error fetching promo data: ${response.statusText}`);
    }
    
    const rawData = await response.json();
    
    // Transform the data to match our PromoData interface
    return rawData.map((item: any) => ({
      id: item.id,
      user_id: item.user_id,
      promo_code: item.promo_code,
      stripe_coupon_id: item.stripe_coupon_id,
      applied_at: item.applied_at,
      checkout_session_id: item.checkout_session_id,
      subscription_id: item.subscription_id,
      status: item.status || 'unknown',
      metadata: item.metadata || {},
      created_at: item.created_at,
      updated_at: item.updated_at,
      users: {
        email: 'User ' + item.user_id.substring(0, 8),
        full_name: 'User'
      }
    }));
  }
  
  useEffect(() => {
    async function loadPromoData() {
      try {
        const promoData = await fetchPromoCodeData();
        logger.info(`Fetched ${promoData.length} promo code records`);
        
        setPromoData(promoData);
        
        // Prepare chart data
        prepareChartData(promoData);
      } catch (err: any) {
        logger.error("Error fetching promo data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    loadPromoData();
  }, []);

  // Prepare chart data for visualization
  const prepareChartData = (data: PromoData[]) => {
    // Group by day for time series
    const byDay = data.reduce((acc: Record<string, any>, promo) => {
      const date = new Date(promo.created_at).toISOString().split('T')[0];
      if (!acc[date]) acc[date] = { date, count: 0, applied: 0 };
      acc[date].count++;
      if (promo.status === 'applied') acc[date].applied++;
      return acc;
    }, {});
    
    // Convert to array for chart
    const timeSeriesData = Object.values(byDay).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    setChartData(timeSeriesData);
    
    // Status distribution for pie chart
    const statusCounts = data.reduce((acc: Record<string, number>, promo) => {
      if (!acc[promo.status]) acc[promo.status] = 0;
      acc[promo.status]++;
      return acc;
    }, {});
    
    const statusChartData = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value
    }));
    
    setStatusData(statusChartData);
  };

  // Calculate statistics
  const totalPromos = promoData.length;
  const appliedPromos = promoData.filter(p => p.status === "applied").length;
  const pendingPromos = promoData.filter(p => p.status === "pending").length;
  const abandonedPromos = promoData.filter(p => p.status === "abandoned").length;
  const conversionRate = totalPromos ? ((appliedPromos / totalPromos) * 100).toFixed(1) : "0";

  // Colors for pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Status badge color mapping
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'applied':
        return "bg-green-100 text-green-800";
      case 'pending':
        return "bg-yellow-100 text-yellow-800";
      case 'failed':
        return "bg-red-100 text-red-800";
      case 'abandoned':
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8">
        <h1 className="text-2xl font-bold mb-6">Promo Code Analytics</h1>
      
      <Tabs defaultValue="overview" onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="charts">Charts</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Promos</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{totalPromos}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Applied</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{appliedPromos}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Abandoned</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{abandonedPromos}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{conversionRate}%</p>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Recent Promo Code Usage</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p>Loading promo data...</p>
              ) : error ? (
                <p className="text-red-500">Error: {error}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Promo Code</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applied At</TableHead>
                      <TableHead>Coupon ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoData.slice(0, 5).map((promo) => (
                      <TableRow key={promo.id}>
                        <TableCell>{promo.promo_code}</TableCell>
                        <TableCell>{promo.users?.email || "Unknown"}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(promo.status)}>
                            {promo.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{promo.applied_at ? formatDate(promo.applied_at) : "-"}</TableCell>
                        <TableCell>{promo.stripe_coupon_id || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Promo Code Usage Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="count" name="Total" fill="#8884d8" />
                      <Bar dataKey="applied" name="Applied" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>All Promo Code Usage</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p>Loading promo data...</p>
              ) : error ? (
                <p className="text-red-500">Error: {error}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Promo Code</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Applied At</TableHead>
                      <TableHead>Coupon ID</TableHead>
                      <TableHead>Subscription ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoData.map((promo) => (
                      <TableRow key={promo.id}>
                        <TableCell>{promo.promo_code}</TableCell>
                        <TableCell>{promo.users?.email || "Unknown"}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeClass(promo.status)}>
                            {promo.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(promo.created_at)}</TableCell>
                        <TableCell>{promo.applied_at ? formatDate(promo.applied_at) : "-"}</TableCell>
                        <TableCell>{promo.stripe_coupon_id || "-"}</TableCell>
                        <TableCell>{promo.subscription_id || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PromoCodeAnalytics;
